import { motion } from "framer-motion";
import { scrollToSection } from "@/components/Nav";
import KeyButton from "@/components/KeyButton";

const EASE = [0.16, 1, 0.3, 1];

const CameraButton = ({ onClick }) => (
  <button
    data-testid="hero-cta-primary"
    onClick={onClick}
    aria-label="View work"
    className="group block w-[190px] select-none outline-none transition-transform duration-500 ease-out hover:-translate-y-1 hover:-rotate-2 focus-visible:-translate-y-1 sm:w-[220px]"
  >
    <div className="glass-tile rounded-[16px] p-[8px] transition-all duration-500 group-hover:shadow-[0_26px_45px_-18px_rgba(13,13,13,0.4)]">
      <div className="rounded-[8px] bg-[#101010] p-[6px] shadow-[inset_0_1px_3px_rgba(0,0,0,0.9)]">
        <div className="relative flex aspect-[4/3] items-center justify-center overflow-hidden rounded-[3px] bg-black">
          <img
            src="/platinum-cd.jpg"
            alt=""
            className="animate-vinyl h-full w-full object-cover"
          />
          <span
            className="absolute inset-0 m-auto flex aspect-square h-[36%] flex-col items-center justify-center rounded-full"
            style={{
              background:
                "conic-gradient(from 210deg,#e4e4e4,#b9b9b9,#f0f0f0,#c4c4c4,#e4e4e4)",
              boxShadow:
                "0 0 0 2px rgba(15,15,15,0.85), inset 0 1px 2px rgba(255,255,255,0.9)",
            }}
          >
            <span className="block font-mono-label text-[7px] font-bold leading-[1.35] tracking-[0.2em] text-[#111111]">
              VIEW
            </span>
            <span className="block font-mono-label text-[7px] font-bold leading-[1.35] tracking-[0.2em] text-[#111111]">
              WORK
            </span>
          </span>
          <span className="absolute left-1.5 top-1.5 h-1 w-1 rounded-full bg-red-500/90 opacity-80 transition-opacity duration-300 group-hover:animate-pulse" />
        </div>
      </div>
      <div className="mt-[6px] flex items-center justify-between px-[4px] pb-[2px]">
        <span
          className="block h-[16px] w-[16px] rounded-full shadow-[inset_0_1px_2px_rgba(255,255,255,0.9),0_1px_2px_rgba(0,0,0,0.35)]"
          style={{
            background:
              "conic-gradient(from 210deg,#d8d8d5,#9a9a97,#e9e9e6,#a5a5a2,#d8d8d5)",
          }}
        />
        <span
          className="block h-[7px] w-[7px] rounded-full shadow-[inset_0_1px_1px_rgba(255,255,255,0.8)]"
          style={{ background: "radial-gradient(circle at 35% 30%,#f0f0ee,#8f8f8c)" }}
        />
        <span className="block h-[11px] w-[4px] rounded-[2px] bg-[#8d8d8a] shadow-[inset_0_1px_1px_rgba(255,255,255,0.7)]" />
      </div>
    </div>
  </button>
);

const MaskedLine = ({ children, delay, className }) => (
  <span className={`block overflow-hidden ${className || ""}`}>
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

export default function Hero() {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative flex min-h-screen flex-col justify-center overflow-hidden px-6 pt-28 pb-16 sm:px-10"
    >
      <div className="mx-auto w-full max-w-[1400px]">
        <motion.div
          initial={{ clipPath: "inset(0 0 100% 0)" }}
          animate={{ clipPath: "inset(0 0 0% 0)" }}
          transition={{ duration: 1.2, delay: 0.5, ease: EASE }}
          className="mt-8 sm:mt-10"
        >
          <img
            src="/logo-black.png"
            alt="Leevé Media"
            className="w-full max-w-3xl xl:max-w-4xl"
          />
        </motion.div>

        <h1
          data-testid="hero-headline-title"
          className="mt-10 max-w-3xl font-helvetica sm:mt-14"
        >
          <MaskedLine
            delay={0.9}
            className="text-4xl font-bold leading-[1.05] tracking-tight text-[#0D0D0D] sm:text-5xl lg:text-6xl"
          >
            Hello!
          </MaskedLine>
          <MaskedLine
            delay={1.05}
            className="text-4xl font-bold leading-[1.05] tracking-tight text-[#0D0D0D] sm:text-5xl lg:text-6xl"
          >
            We are Leevé media
          </MaskedLine>
        </h1>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.45, ease: EASE }}
          className="mt-6 max-w-lg font-helvetica"
        >
          <p
            data-testid="hero-description-text"
            className="font-helvetica text-base font-bold leading-snug tracking-tight text-[#0D0D0D] sm:text-lg"
          >
            Grow and sell on social media as a busy business owner or a
            creator.
          </p>
          <p className="mt-3 font-helvetica text-sm leading-relaxed text-[#4A4944] sm:text-base">
            We help businesses grow through organic content that drives traffic
            and builds credibility, while helping creators build strong
            personal brands that stand out online. From content strategy and
            ideation to editing and posting, we handle it all — building a
            social presence that earns trust, attracts the right audience, and
            generates real opportunities beyond just likes.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.6, ease: EASE }}
          className="mt-10 flex flex-wrap items-end gap-6"
        >
          <CameraButton onClick={() => scrollToSection("#work")} />
          <KeyButton
            testid="hero-cta-secondary"
            onClick={() => scrollToSection("#contact")}
            className="mb-1"
          >
            GET IN TOUCH
          </KeyButton>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.9 }}
          className="mt-16 flex items-center justify-between border-t border-[#0D0D0D]/10 pt-5 font-mono-label text-[10px] tracking-[0.25em] text-[#8C8B85] sm:mt-24"
        >
          <span>@LEEVE.MEDIA</span>
          <span className="hidden sm:block">PORTFOLIO — VOL. 01</span>
          <span>SCROLL ↓</span>
        </motion.div>
      </div>
    </section>
  );
}
